import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'app/store';
import type { Participant } from './types';

const selectInvitorState = (state: RootState) => state.invitor;

// dminor is the MinorDefense brand — its family-management routing differs
// from every other brand: aged-out children must surface in a dedicated
// "new adult" section (so a primary user can invite them to SecureStart)
// rather than being hidden. Other brands hide them outright.
const DMINOR_BRAND_CODE = 'dminor';

function sortByFirstName(a: Participant, b: Participant): number {
  const aName = (a.name?.first ?? '').toLowerCase();
  const bName = (b.name?.first ?? '').toLowerCase();
  if (aName < bName) return -1;
  if (aName > bName) return 1;
  return 0;
}

export const selectInvitor = createSelector(
  [selectInvitorState],
  (s) => s.invitor,
);

export const selectInvitorLoaded = createSelector(
  [selectInvitorState],
  (s) => s.hasLoaded,
);

const selectParticipants = createSelector(
  [selectInvitor],
  (invitor): Participant[] => invitor?.participants ?? [],
);

// Exported alias — the raw list of every invitor participant. Prefer
// the section-scoped factories above for plan-members rendering; this
// one exists for cross-cutting scans (e.g. the post-sign-in aged-out
// popup on the dashboard).
export const selectInvitorParticipants = selectParticipants;

// Children only, excluding aged-out, sorted by first name (matches Angular
// getParticipantsOfType('child')). Prefer `selectChildSectionParticipants`
// in plan-members code — that variant handles the dminor brand's aged-out
// routing rule instead of silently dropping those participants.
export const selectChildParticipants = createSelector(
  [selectParticipants],
  (participants) =>
    [...participants]
      .filter((p) => p.participantType === 'child' && p.status !== 'aged out')
      .sort(sortByFirstName),
);

// Single participant by id (matches Angular getChildParticipant).
export const makeSelectParticipantById = (id: string) =>
  createSelector([selectParticipants], (participants) =>
    participants.find((p) => p.id === id),
  );

// Children the plan-members page should render in the "Children" section.
// Mirrors Angular `plan-members.component.ts:104-117`:
//   • dminor        → children WHERE status !== 'aged out'
//                     (aged-out children move to the new-adult section)
//   • every other   → ALL children (aged-out included) — Angular's
//     brand         `_filter(res, ['participantType', 'child'])`
//
// Returns a factory selector so callers pass the current brandCode.
// Earlier iteration inverted the rule and hid aged-out children on
// non-dminor brands too, which caused the plan-members page to display
// fewer children than Angular for the same user.
export function selectChildSectionParticipants(brandCode: string | undefined) {
  const isDminor = brandCode === DMINOR_BRAND_CODE;
  return createSelector([selectParticipants], (participants) => {
    const filtered = participants.filter((p) => {
      if (p.participantType !== 'child') return false;
      if (isDminor && p.status === 'aged out') return false;
      return true;
    });
    return [...filtered].sort(sortByFirstName);
  });
}

// Adults (excluding the primary user's own record if the backend embeds
// it). Sorted by first name for a stable render order.
export const selectAdultParticipants = createSelector(
  [selectParticipants],
  (participants) =>
    [...participants]
      .filter((p) => p.participantType === 'adult')
      .sort(sortByFirstName),
);

// "New adult" section entries. Angular parity
// (plan-members.component.ts:109-113):
//   • dminor        → `participantType === 'new adult'`  OR
//                     `participantType === 'child' && status === 'aged out'`
//   • every other   → `participantType === 'new adult'`
//     brand
export function selectNewAdultSectionParticipants(
  brandCode: string | undefined,
) {
  const isDminor = brandCode === DMINOR_BRAND_CODE;
  return createSelector([selectParticipants], (participants) => {
    const filtered = participants.filter((p) => {
      if (p.participantType === 'new adult') return true;
      if (isDminor && p.participantType === 'child' && p.status === 'aged out')
        return true;
      return false;
    });
    return [...filtered].sort(sortByFirstName);
  });
}

export interface InvitorLimits {
  maxAdults: number;
  maxChildren: number;
  isAdultLimitReached: boolean;
  isChildLimitReached: boolean;
}

// Adult / child slot limits from the invitor payload. Drives the Add
// Member modal's initial-state machine (see `resolveInitialState`).
export const selectInvitorLimits = createSelector(
  [selectInvitor],
  (invitor): InvitorLimits => ({
    maxAdults: invitor?.maxAdults ?? 0,
    maxChildren: invitor?.maxChildren ?? 0,
    isAdultLimitReached: !!invitor?.isAdultLimitReached,
    isChildLimitReached: !!invitor?.isChildLimitReached,
  }),
);
